import { useEffect } from "react";

const BASE_URL = "https://jiia-studio.com";
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-jiia-studio.jpg`;

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string | undefined;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | undefined;
  ogImage?: string | undefined;
  ogType?: string | undefined;
  noindex?: boolean | undefined;
  breadcrumbs?: { name: string; path: string }[] | undefined;
}

const SEOHead = ({ title, description, canonical, jsonLd, ogImage, ogType = "website", noindex = false, breadcrumbs }: SEOHeadProps) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // Standard meta
    setMeta("description", description);
    setMeta("robots", noindex ? "noindex, nofollow" : "index, follow");

    // Open Graph
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", ogType, true);
    setMeta("og:site_name", "JIIA Studio", true);
    setMeta("og:locale", "fr_FR", true);
    if (canonical) setMeta("og:url", canonical.startsWith("http") ? canonical : `${BASE_URL}${canonical}`, true);
    setMeta("og:image", ogImage || DEFAULT_OG_IMAGE, true);

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage || DEFAULT_OG_IMAGE);

    // Canonical
    if (canonical) {
      const href = canonical.startsWith("http") ? canonical : `${BASE_URL}${canonical}`;
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = href;
    }

    // JSON-LD
    const existingScript = document.querySelector('script[data-seo-jsonld]');
    if (existingScript) existingScript.remove();

    const allLd: Record<string, unknown>[] = [];
    if (jsonLd) {
      const data = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      allLd.push(...data);
    }
    if (breadcrumbs && breadcrumbs.length > 0) {
      allLd.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: b.name,
          item: b.path.startsWith("http") ? b.path : `${BASE_URL}${b.path}`,
        })),
      });
    }
    if (allLd.length > 0) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.textContent = JSON.stringify(allLd.length === 1 ? allLd[0] : allLd);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.querySelector('script[data-seo-jsonld]');
      if (s) s.remove();
    };
  }, [title, description, canonical, jsonLd, ogImage, ogType, noindex, breadcrumbs]);

  return null;
};

export default SEOHead;
